import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';

export interface SignInRequest {
  username: string;
  password: string;
}

export interface UserDto {
  id: number;
  email: string;
  fullName: string;
  phone?: string;
  role?: {
    id: number;
    name: string;
    code: string;
  };
  /** Сотрудник компании (на сервере у него роль ROLE_X_EMPLOYEE). */
  employee?: boolean;
  /** Настоящий код роли с сервера; role.code у сотрудника приведён к базовому ROLE_X. */
  actualRoleCode?: string;
}

const EMPLOYEE_SUFFIX = '_EMPLOYEE';

/**
 * Сотрудник компании (ROLE_FREIGHT_FORWARDER_EMPLOYEE и т.д.) работает с теми же разделами платформы, что и
 * администратор компании (ROLE_FREIGHT_FORWARDER), а различие в правах задают права в компании. Поэтому для маршрутов и
 * меню код роли приводится к базовому, а настоящий сохраняется в actualRoleCode.
 */
export function normalizeUser<T extends UserDto | null | undefined>(user: T): T {
  const code = user?.role?.code;
  if (!user || !user.role || !code || !code.endsWith(EMPLOYEE_SUFFIX) || user.employee) {
    return user;
  }
  return {
    ...user,
    role: { ...user.role, code: code.slice(0, -EMPLOYEE_SUFFIX.length) },
    employee: true,
    actualRoleCode: code
  };
}

export interface JwtAuthenticationResponse {
  token: string;
  user: UserDto;
}

/** Ответ «Войти как»: токен пользователя, его данные и момент окончания токена (ISO). */
export interface ImpersonationResponse extends JwtAuthenticationResponse {
  expiresAt: string;
}

/** Кто и под кем сейчас работает; показывается в плашке «Вы действуете как …». */
export interface ImpersonationInfo {
  adminName: string;
  targetName: string;
  /** Момент окончания токена «Войти как» (ISO). */
  expiresAt: string;
}

/** Окончание логина гостевой записи (клиент, который ещё не ввёл телефон). */
export const GUEST_EMAIL_SUFFIX = '@guest.bilload.kz';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = '/cargo/auth';
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';
  // «Войти как»: сессия администратора откладывается сюда, пока он действует от имени клиента
  private readonly ADMIN_TOKEN_KEY = 'impersonation_admin_token';
  private readonly ADMIN_USER_KEY = 'impersonation_admin_user';
  private readonly IMPERSONATION_KEY = 'impersonation_info';

  constructor(private http: HttpClient) {}

  signIn(request: SignInRequest): Observable<JwtAuthenticationResponse> {
    return this.http.post<JwtAuthenticationResponse>(`${this.apiUrl}/sign-in`, request).pipe(
      tap(response => this.saveSession(response))
    );
  }

  /** Запоминает токен и пользователя (после входа или смены логина гостя на телефон). */
  saveSession(response: JwtAuthenticationResponse): void {
    // Защита от SSR - на сервере нет localStorage
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    response.user = normalizeUser(response.user);
    if (response.token) {
      localStorage.setItem(this.TOKEN_KEY, response.token);
    } else {
      console.error('AuthService: No token in response!', response);
    }
    if (response.user) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(response.user));
    }
  }

  /** Гость — клиент, который ещё не ввёл телефон: логин вида «uuid@guest.bilload.kz». */
  isGuest(): boolean {
    return !!this.getUser()?.email?.endsWith(GUEST_EMAIL_SUFFIX);
  }

  getToken(): string | null {
    // Защита от SSR - на сервере нет localStorage
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getUser(): UserDto | null {
    // Защита от SSR - на сервере нет localStorage
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    const userStr = localStorage.getItem(this.USER_KEY);
    if (userStr) {
      try {
        return normalizeUser(JSON.parse(userStr));
      } catch {
        return null;
      }
    }
    return null;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  logout(): void {
    // Защита от SSR - на сервере нет localStorage
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('AuthService: Cannot clear token - localStorage not available (SSR?)');
      return;
    }
    console.log('AuthService: Clearing token and user from localStorage');
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    // Выход из режима «Войти как» тоже полный: отложенную сессию администратора не оставляем
    localStorage.removeItem(this.ADMIN_TOKEN_KEY);
    localStorage.removeItem(this.ADMIN_USER_KEY);
    localStorage.removeItem(this.IMPERSONATION_KEY);
  }

  // ── «Войти как» (только администратор, временная функция поддержки) ─────────

  /** Войти под пользователем: админская сессия откладывается, дальше все запросы идут от имени пользователя. */
  startImpersonation(userId: number): Observable<ImpersonationResponse> {
    return this.http.post<ImpersonationResponse>(`/cargo/api/admin/impersonation/${userId}`, {}).pipe(
      tap(response => {
        if (typeof window === 'undefined' || !window.localStorage || !response.token) {
          return;
        }
        const adminToken = this.getToken();
        const admin = this.getUser();
        if (adminToken && admin) {
          localStorage.setItem(this.ADMIN_TOKEN_KEY, adminToken);
          localStorage.setItem(this.ADMIN_USER_KEY, JSON.stringify(admin));
        }
        this.saveSession(response);
        const info: ImpersonationInfo = {
          adminName: admin?.fullName || admin?.email || '',
          targetName: response.user?.fullName || response.user?.email || '',
          expiresAt: response.expiresAt
        };
        localStorage.setItem(this.IMPERSONATION_KEY, JSON.stringify(info));
      })
    );
  }

  isImpersonating(): boolean {
    return this.getImpersonation() !== null;
  }

  getImpersonation(): ImpersonationInfo | null {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    const raw = localStorage.getItem(this.IMPERSONATION_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as ImpersonationInfo;
    } catch {
      return null;
    }
  }

  /** Вернуться в сессию администратора. false — отложенной сессии нет (нужен обычный вход). */
  stopImpersonation(): boolean {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    const adminToken = localStorage.getItem(this.ADMIN_TOKEN_KEY);
    const adminUser = localStorage.getItem(this.ADMIN_USER_KEY);
    localStorage.removeItem(this.ADMIN_TOKEN_KEY);
    localStorage.removeItem(this.ADMIN_USER_KEY);
    localStorage.removeItem(this.IMPERSONATION_KEY);
    if (!adminToken || !adminUser) {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
      return false;
    }
    localStorage.setItem(this.TOKEN_KEY, adminToken);
    localStorage.setItem(this.USER_KEY, adminUser);
    return true;
  }
}
